import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
// hooks
import useGetFetch from "../../../hooks/useGetFetch";
// global
import { dbDataStore } from "../../../store/Store";
// components
import Loading from "../../../components/Loading";
import ErrorDisplay from "../../../components/ErrorDisplay";
import InputField from "../../../formComponents/InputField";
import InvoiceDetailFilter from "../../vendor-invoices/InvoiceDetailFilter";
import DeliveredDate from "../../../components/DeliveredDate";

export default function AccountCustomerInvoiceDetails() {
  //global
  const projectData = dbDataStore((data) => data.projectData);
  // local
  const { invId } = useParams("invId");
  const [invoiceDetails, setInvoiceDetails] = useState();
  const [mismatchValue, setMismatchValue] = useState(2.0);
  const [visibleLoads, setVisibleLoads] = useState([]);
  const [canceled, setCanceled] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loads, setLoads] = useState([]);

  const { isPending, error, statusCode } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/account-customer-invoices/${invId}`,
    setInvoiceDetails
  );
  // freight load filtering
  useEffect(() => {
    if (invoiceDetails && invoiceDetails.customer_invoice_loads) {
      const canceledLoads = [];
      const regular = [];
      invoiceDetails.customer_invoice_loads.forEach((l) => {
        if (
          invoiceDetails.project_loads &&
          !invoiceDetails.customer_invoice.is_tier
        ) {
          var projectLoad = invoiceDetails.project_loads.filter(
            (x) => x.id === l.load_id
          );
          if (projectLoad.length !== 0 && projectLoad[0].is_canceled) {
            canceledLoads.push(l);
          } else {
            regular.push(l);
          }
        } else {
          regular.push(l);
        }
      });
      if (regular.length > 0) {
        setLoads(regular);
        setVisibleLoads(regular);
      } else {
        setLoads(invoiceDetails.customer_invoice_loads);
        setVisibleLoads(invoiceDetails.customer_invoice_loads);
      }
      setCanceled(canceledLoads);
    }
  }, [invoiceDetails, setCanceled, setLoads, setVisibleLoads]);

  // asset load filtering
  useEffect(() => {
    if (
      invoiceDetails &&
      invoiceDetails.customer_invoice_loads &&
      invoiceDetails.project_asset_loads
    ) {
      const regular = [];
      invoiceDetails.customer_invoice_loads.forEach((l) => {
        var projectLoad = invoiceDetails.project_asset_loads.filter(
          (x) => x.id === l.load_id
        );
        if (projectLoad.length !== 0) {
          regular.push(l);
        }
      });
      setLoads(regular);
      setVisibleLoads(regular);
    }
  }, [invoiceDetails, setLoads, setVisibleLoads]);

  const calcTotals = (invLoad) => {
    var loadTotals = {
      weight: 0,
      weight_diff: 0,
      total_rev: 0,
      calc_rev: 0,
      rev_diff: 0,
    };
    var load;
    if (
      !invoiceDetails.customer_invoice.is_asset &&
      invoiceDetails.project_loads
    ) {
      load = invoiceDetails.project_loads.filter(
        (x) => x.id === invLoad.load_id
      );
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.total_rev =
          invLoad.freight_rev + invLoad.fsc_rev + invLoad.sand_rev;
        loadTotals.calc_rev =
          load[0].freight_rev + load[0].fsc_rev + load[0].sand_rev;
        loadTotals.rev_diff = loadTotals.total_rev - loadTotals.calc_rev;
      }
    } else if (
      invoiceDetails.customer_invoice.is_asset &&
      invoiceDetails.project_asset_loads
    ) {
      load = invoiceDetails.project_asset_loads.filter(
        (x) => x.id === invLoad.load_id
      );
      if (load.length > 0) {
        loadTotals.weight = load[0].weight_tons;
        loadTotals.weight_diff = invLoad.weight_tons - load[0].weight_tons;
        loadTotals.total_rev = invLoad.freight_rev + invLoad.fsc_rev;
        loadTotals.calc_rev = load[0].freight_rev + load[0].fsc_rev;
        loadTotals.rev_diff = loadTotals.total_rev - loadTotals.calc_rev;
      }
    }

    return loadTotals;
  };

  const handleMismatch = (revDiff) => {
    if (Math.abs(revDiff) > mismatchValue) {
      return "Yes";
    } else {
      return "";
    }
  };

  if (isPending) {
    return <Loading />;
  } else if (error) {
    return <ErrorDisplay message={error} code={statusCode} />;
  } else if (invoiceDetails) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-between project-header">
          <h2>Invoice : {invoiceDetails.customer_invoice.invoice_name}</h2>
          {!invoiceDetails.customer_invoice.is_tier && (
            <InputField
              name={"mismatch-value"}
              title={"Mismatch Threshold $"}
              value={mismatchValue}
              type={"number"}
              increment={0.01}
              onChange={(e) => setMismatchValue(e.target.value)}
              required
            />
          )}
        </div>
        {canceled.length !== 0 && (
          <>
            <div className="d-flex justify-content-between project-header">
              <h4>Canceled Loads</h4>
            </div>
            <div className="load-display-list">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Load</th>
                    <th>Job</th>
                    <th>Delivered</th>
                    {!invoiceDetails.customer_invoice.is_asset && (
                      <th>Ticket</th>
                    )}
                    <th>Weight</th>
                    <th>Calc Weight</th>
                    <th>Weight Diff</th>
                    {!invoiceDetails.customer_invoice.is_asset && (
                      <th>Product</th>
                    )}
                    <th>Freight Rev</th>
                    <th>FSC Rev</th>
                    {!invoiceDetails.customer_invoice.is_asset && (
                      <th>Sand Rev</th>
                    )}
                    <th>Total Rev</th>
                    <th>Calc Rev</th>
                    <th>Rev Diff</th>
                    <th>Mismatch</th>
                  </tr>
                </thead>
                <tbody>
                  {canceled.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>
                        {
                          projectData.filter((x) => x.id === l.project_id)[0]
                            .job_name
                        }
                      </td>
                      <DeliveredDate d={l.delivered_date} />
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? calcTotals(l).weight.toFixed(2)
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? calcTotals(l).weight_diff.toFixed(2)
                          : "Closed"}
                      </td>
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>{l.product_name}</td>
                      )}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>${l.sand_rev.toFixed(2)}</td>
                      )}
                      <td>${calcTotals(l).total_rev.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `$${calcTotals(l).calc_rev.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `$${calcTotals(l).rev_diff.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `${handleMismatch(calcTotals(l).rev_diff)}`
                          : "Closed"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="d-flex justify-content-between project-header">
          {loads.length !== 0 && <h4>Loads</h4>}
        </div>
        {loads.length !== 0 && !invoiceDetails.customer_invoice.is_tier && (
          <div className="row g-2">
            <InvoiceDetailFilter
              isCustomer={true}
              customerFilter={filter}
              setCustomerfilter={setFilter}
            />
          </div>
        )}
        <div className="load-display-list">
          {loads.length !== 0 && !invoiceDetails.customer_invoice.is_tier ? (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Job</th>
                  <th>Delivered</th>
                  {!invoiceDetails.customer_invoice.is_asset && <th>Ticket</th>}
                  <th>Weight</th>
                  <th>Calc Weight</th>
                  <th>Weight Diff</th>
                  {!invoiceDetails.customer_invoice.is_asset && (
                    <th>Product</th>
                  )}
                  <th>Freight Rev</th>
                  <th>FSC Rev</th>
                  {!invoiceDetails.customer_invoice.is_asset && (
                    <th>Sand Rev</th>
                  )}
                  <th>Total Rev</th>
                  <th>Calc Rev</th>
                  <th>Rev Diff</th>
                  <th>Mismatch</th>
                </tr>
              </thead>
              <tbody>
                {visibleLoads.map((l, idx) =>
                  filter !== "cost mismatch" ? (
                    <tr key={idx}>
                      <td>{l.load_no}</td>
                      <td>
                        {
                          projectData.filter((x) => x.id === l.project_id)[0]
                            .job_name
                        }
                      </td>
                      <DeliveredDate d={l.delivered_date} />
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>{l.ticket_number}</td>
                      )}
                      <td>{l.weight_tons.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? calcTotals(l).weight.toFixed(2)
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? calcTotals(l).weight_diff.toFixed(2)
                          : "Closed"}
                      </td>
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>{l.product_name}</td>
                      )}
                      <td>${l.freight_rev.toFixed(2)}</td>
                      <td>${l.fsc_rev.toFixed(2)}</td>
                      {!invoiceDetails.customer_invoice.is_asset && (
                        <td>${l.sand_rev.toFixed(2)}</td>
                      )}
                      <td>${calcTotals(l).total_rev.toFixed(2)}</td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `$${calcTotals(l).calc_rev.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `$${calcTotals(l).rev_diff.toFixed(2)}`
                          : "Closed"}
                      </td>
                      <td>
                        {invoiceDetails.customer_invoice.invoice_status_id !==
                        22
                          ? `${handleMismatch(calcTotals(l).rev_diff)}`
                          : "Closed"}
                      </td>
                    </tr>
                  ) : (
                    filter === "cost mismatch" &&
                    Math.abs(calcTotals(l).rev_diff) > mismatchValue && (
                      <tr key={idx}>
                        <td>{l.load_no}</td>
                        <td>
                          {
                            projectData.filter((x) => x.id === l.project_id)[0]
                              .job_name
                          }
                        </td>
                        <DeliveredDate d={l.delivered_date} />
                        {!invoiceDetails.customer_invoice.is_asset && (
                          <td>{l.ticket_number}</td>
                        )}
                        <td>{l.weight_tons.toFixed(2)}</td>
                        <td>
                          {invoiceDetails.customer_invoice.invoice_status_id !==
                          22
                            ? calcTotals(l).weight.toFixed(2)
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.customer_invoice.invoice_status_id !==
                          22
                            ? calcTotals(l).weight_diff.toFixed(2)
                            : "Closed"}
                        </td>
                        {!invoiceDetails.customer_invoice.is_asset && (
                          <td>{l.product_name}</td>
                        )}
                        <td>${l.freight_rev.toFixed(2)}</td>
                        <td>${l.fsc_rev.toFixed(2)}</td>
                        {!invoiceDetails.customer_invoice.is_asset && (
                          <td>${l.sand_rev.toFixed(2)}</td>
                        )}
                        <td>${calcTotals(l).total_rev.toFixed(2)}</td>
                        <td>
                          {invoiceDetails.customer_invoice.invoice_status_id !==
                          22
                            ? `$${calcTotals(l).calc_rev.toFixed(2)}`
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.customer_invoice.invoice_status_id !==
                          22
                            ? `$${calcTotals(l).rev_diff.toFixed(2)}`
                            : "Closed"}
                        </td>
                        <td>
                          {invoiceDetails.customer_invoice.invoice_status_id !==
                          22
                            ? `${handleMismatch(calcTotals(l).rev_diff)}`
                            : "Closed"}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          ) : (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Load</th>
                  <th>Job</th>
                  <th>Project Invoice</th>
                  <th>Delivered</th>
                  <th>Mileage</th>
                  <th>Ticket</th>
                  <th>Weight</th>
                  <th>Carrier Name</th>
                  <th>Driver Name</th>
                  <th>Terminal</th>
                  <th>Product</th>
                  <th>Tier Rev</th>
                  <th>Billed Rev</th>
                  <th>Total Rev</th>
                </tr>
              </thead>
              <tbody>
                {visibleLoads.map((l, idx) =>
                  <tr key={idx}>
                    <td>{l.load_no}</td>
                    <td>{l.project_name}</td>
                    <td>{l.invoice_name}</td>
                    <DeliveredDate d={l.delivered_date} />
                    <td>{l.mileage}</td>
                    <td>{l.ticket_number}</td>
                    <td>{l.weight_tons}</td>
                    <td>{l.carrier_name}</td>
                    <td>{l.driver_name}</td>
                    <td>{l.terminal_name}</td>
                    <td>{l.product_name}</td>
                    <td>${l.freight_rev.toFixed(2)}</td>
                    <td>${l.project_inv_rev.toFixed(2)}</td>
                    <td>${l.total_rev.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
}
